import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { Button, Size, Type, WiconsName, IconsLibrary } from '@platform/witems';
import {
  getSmartMessages, byLastInteractionDesc, conversationTimeMs, careTeamSpokeLast, conversationTimeLabel,
  engageListSubtitle,
} from '../logic/engage';
import { useFetchDashboardView, useRefreshUnreadCount, useAdjustUnreadCount } from '../state/data/dashboard';
import { usePatient, usePatientRecord } from '../state/data/patient';
import { Constants } from '../config/constants';
import { resolvePatientPhone } from '../logic/patientContact';
import useSpruceCall from '../hooks/useSpruceCall';
import { useMarkChatViewed, useMarkChatUnread, useLastInteractionDates } from '../state/data/outreach';
import { engageRosterScope } from '../config/program';
import { journeyGroups, stepEmoji, lastInboundViaClinicLine } from '../logic/dashboard';
import { stepSelection } from '../logic/engage';
import useDemoMode from '../hooks/useDemoMode';
import useNow from '../hooks/useNow';
import ChatPanel from './ChatPanel';
import CallModal from './CallModal';
import Media from './Media';
import BpValue from './BpValue';
import useCallStore from '../state/application/useCallStore';
import { timeAgo } from '../utils/formatTime';
import useIdleAware from '../hooks/useIdleAware';

// Tag tone per engagement bucket (labels + counts come from the server envelope); unknown → neutral.
const ENGAGEMENT_TONE = { unread: 'info', stuck_onboarding: 'bad', quiet_7d: 'moderate' };
const toneOf = (value) => ENGAGEMENT_TONE[value] ?? 'neutral';

// The view's max limit. Sitting on it keeps page 1 == the whole roster, so the
// client-side recency sort below is exact rather than per-page.
const PAGE_SIZE = 200;
const REFRESH_INTERVAL_MS = 60000; // 60s — background refresh of page 0 + filter counts
const IDLE_THRESHOLD_MS = 120000;  // 2min idle → pause

// Both Engage menus carry `.engage-popover`; the list's keyboard shortcuts stand down while one is open.
const POPOVER_OPEN = '.engage-popover';

// Portaled: the list scroller (overflow-y: auto) would clip it.
function RowKebabMenu({ hasUnread, onMarkUnread, onMarkRead }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = (e) => {
    e.stopPropagation();
    if (open) { setOpen(false); return; }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ left: Math.max(8, rect.right - 200), top: rect.bottom + 4 });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector('[role="menuitem"]')?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    };
    const onClick = (e) => {
      if (buttonRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // The anchor is measured once: a scroll that moves the row (not the chat's own) closes the menu.
    const onScroll = (e) => { if (e.target instanceof Node && e.target.contains(buttonRef.current)) setOpen(false); };
    const close = () => setOpen(false);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  // The chosen item unmounts; hand focus back to the kebab rather than <body>.
  const choose = (e, fn) => {
    e.stopPropagation();
    fn();
    setOpen(false);
    buttonRef.current?.focus({ preventScroll: true });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="engage-row-kebab"
        aria-label="Message actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        ⋮
      </button>
      {open && anchor && createPortal(
        <div
          ref={menuRef}
          className="profile-menu engage-popover"
          role="menu"
          style={{ left: anchor.left, top: anchor.top, minWidth: 200 }}
          onClick={(e) => e.stopPropagation()}
        >
          {hasUnread ? (
            <button role="menuitem" className="profile-menu-item" onClick={(e) => choose(e, onMarkRead)}>
              Mark as read
            </button>
          ) : (
            <button role="menuitem" className="profile-menu-item" onClick={(e) => choose(e, onMarkUnread)}>
              Mark as unread
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

function FilterOption({ name, label, count, checked, onSelect }) {
  return (
    <label className={`filter-option${checked ? ' is-checked' : ''}`}>
      <input type="radio" name={name} checked={checked} onChange={onSelect} />
      <span className="engage-filter-menu__label">{label}</span>
      {typeof count === 'number' && <span className="engage-filter-menu__count">{count}</span>}
    </label>
  );
}

// Native radios in a dialog, not role="menu": the arrows move within a group for free,
// and the list's ↑/↓ handler already leaves inputs alone. Portaled for the same reason as the kebab.
function EngageFilterMenu({ buckets, bucket, onBucket, steps, step, onStep, onClear, activeCount, disabled }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setAnchor({ right: Math.max(8, window.innerWidth - rect.right), top: rect.bottom + 6 });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const menu = menuRef.current;
    (menu?.querySelector('input:checked') ?? menu?.querySelector('input'))?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      setOpen(false);
      if (menuRef.current?.contains(document.activeElement)) buttonRef.current?.focus({ preventScroll: true });
    };
    const onDown = (e) => {
      if (buttonRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onResize = () => setOpen(false);
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', onResize);
    };
  }, [open]);

  // Tabbing out leaves no focus inside the portal (or none at all: it is body's last node); close.
  const onBlur = (e) => {
    const next = e.relatedTarget;
    if (next && (menuRef.current?.contains(next) || buttonRef.current?.contains(next))) return;
    setOpen(false);
  };
  // "Clear all" unmounts its own footer; land on "All conversations" instead of <body>.
  const clearAll = () => {
    onClear();
    menuRef.current?.querySelector('input')?.focus({ preventScroll: true });
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`engage-filter-btn${activeCount > 0 ? ' is-active' : ''}`}
        aria-label={activeCount > 0 ? `Filter conversations, ${activeCount} applied` : 'Filter conversations'}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        onClick={toggle}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
          <path d="M4 7h16M7 12h10M10 17h4" />
        </svg>
        {activeCount > 0 && <span className="engage-filter-btn__badge" aria-hidden="true">{activeCount}</span>}
      </button>
      {open && anchor && createPortal(
        <div
          ref={menuRef}
          className="engage-popover engage-filter-menu"
          role="dialog"
          aria-label="Filter conversations"
          tabIndex={-1}
          style={{ top: anchor.top, right: anchor.right }}
          onBlur={onBlur}
        >
          <div role="radiogroup" aria-labelledby="engage-filter-buckets">
            <div id="engage-filter-buckets" className="eyebrow text-secondary engage-filter-menu__title">Conversations</div>
            <FilterOption name="engage-bucket" label="All conversations" checked={!bucket} onSelect={() => onBucket(null)} />
            {buckets.map((o) => (
              <FilterOption
                key={o.value}
                name="engage-bucket"
                label={o.label}
                count={o.count ?? 0}
                checked={bucket === o.value}
                onSelect={() => onBucket(o.value)}
              />
            ))}
          </div>
          {steps.length > 0 && (
            <div role="radiogroup" aria-labelledby="engage-filter-steps">
              <div id="engage-filter-steps" className="eyebrow text-secondary engage-filter-menu__title">Journey step</div>
              <FilterOption name="engage-step" label="Any step" checked={!step} onSelect={() => onStep(null)} />
              {steps.map((g) => (
                <FilterOption
                  key={g.value}
                  name="engage-step"
                  label={`${stepEmoji(g.value)} ${g.label}`}
                  count={g.count}
                  checked={step === g.value}
                  onSelect={() => onStep(g.value)}
                />
              ))}
            </div>
          )}
          {activeCount > 0 && (
            <div className="engage-filter-menu__foot">
              <button type="button" className="filter-clear" onClick={clearAll}>Clear all</button>
            </div>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}

/* ── Patient row ──────────────────────────────────────────────── */
function EngagePatientRow({ patient, isSelected, replied, nowMs, onClick, onToggleUnread, demoMode }) {
  const hasUnread = !!patient.unread?.has_unread;
  // Unread is the dot, never a tag; the other buckets tag only while their filter is on.
  const reason = patient.engageReason && patient.engageReason !== 'unread' ? patient.engageReason : null;
  const name = `${patient.firstname} ${patient.lastname}`.trim();
  const initials = `${patient.firstname?.[0] ?? ''}${patient.lastname?.[0] ?? ''}`.toUpperCase() || '?';
  const snippet = patient.unread?.snippet;
  const clinicLine = lastInboundViaClinicLine(patient.unread);
  const blur = demoMode ? ' demo-blur' : '';

  return (
    <div
      onClick={onClick}
      data-userid={patient.userid}
      aria-current={isSelected ? 'true' : undefined}
      className={`engage-row${hasUnread ? ' is-unread' : ''}${isSelected ? ' is-selected' : ''}`}
    >
      <span className="engage-row__dot" aria-hidden="true" />
      <Media size={40} alt="" fallback={initials} className={`engage-row__avatar${blur}`} />
      <div className="engage-row__body">
        <div className="engage-row__line1">
          {/* A real link: Tab, screen readers and open-in-new-tab reach the chat; a plain click stays in-app. */}
          <Link
            to={`/engage/${encodeURIComponent(patient.userid)}`}
            className={`engage-row__name${blur}`}
            onClick={(e) => {
              e.stopPropagation();
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              onClick();
            }}
          >
            {name}
          </Link>
          {hasUnread && <span className="sr-only">Unread</span>}
          <span className="engage-row__aside">
            {patient.lastInteractionMs != null && (
              <span className="engage-row__time" title={`Last message ${new Date(patient.lastInteractionMs).toLocaleString()}`}>
                {conversationTimeLabel(patient.lastInteractionMs, nowMs)}
              </span>
            )}
            <RowKebabMenu
              hasUnread={hasUnread}
              onMarkUnread={() => onToggleUnread?.(patient.userid, true)}
              onMarkRead={() => onToggleUnread?.(patient.userid, false)}
            />
          </span>
        </div>
        {snippet && (
          <p className="engage-row__preview" title={replied ? 'The care team replied after this message' : undefined}>
            {clinicLine && (
              <>
                <span className="engage-tag engage-tag--moderate" title="Sent to the clinic phone line, which anyone can text">
                  Clinic line · sender unverified
                </span>{' '}
              </>
            )}
            {replied && <span className="engage-row__replied">Replied · </span>}
            <span className={blur.trim() || undefined}>{snippet}</span>
          </p>
        )}
        <div className="engage-row__meta">
          <BpValue systolic={patient.lastSystolic} diastolic={patient.lastDiastolic} empty="No reading yet" />
          {patient.lastEpoch != null && <span title="Last reading">{timeAgo(patient.lastEpoch, nowMs)}</span>}
          {reason && patient.engageReasonLabel && (
            <span className={`engage-tag engage-tag--${toneOf(reason)}`}>{patient.engageReasonLabel}</span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Row mapper: dashboard-view row → flat shape the row component consumes ── */
function rowToPatient(r) {
  return {
    userid: r.userid,
    firstname: r.name?.first || '',
    lastname: r.name?.last || '',
    phone_number: r.phone_number || null,
    lastSystolic: r.bp?.systolic ?? null,
    lastDiastolic: r.bp?.diastolic ?? null,
    lastEpoch: r.lastReading?.epoch ?? null,
    lastHumanOutreachAt: r.lastHumanOutreach?.at ?? null,
    ehr_patient_id: r.ehr_patient_id || null,
    unread: r.unread || null,
    risk_level: r.risk_level || null,
    medication_classes: r.medication_classes || [],
    conditions: r.conditions?.list || [],
  };
}

/* ── Main EngageWorkspace ─────────────────────────────────────── */
export default function EngageWorkspace({ userRole, currentUser, selectedPatientId, onSelectPatient, onOpenPatientFile }) {
  const [engagement, setEngagement] = useState(null);  // null | 'unread' | 'stuck_onboarding' | 'quiet_7d'
  // Journey step (the board's open_task groups), ANDed with the engagement bucket server-side.
  const [step, setStep] = useState(null);
  const [steps, setSteps] = useState([]);
  // The open chat, if it was unread when opened: it sorts as unread until another opens, so it never jumps from under ↑/↓.
  const [heldUnreadId, setHeldUnreadId] = useState(null);
  const [query, setQuery]   = useState('');
  const [rows, setRows]     = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // engagementFilter === null is the "first load" sentinel — the list renders skeleton rows.
  // After the first response lands it never goes back to null, so the 60s background
  // refresh leaves counts + rows visible (no flicker).
  const [engagementFilter, setEngagementFilter] = useState(null);
  // rowsEngagement = the filter the CURRENT rows were fetched for. Tagged-row
  // labels read from this, NOT from the live `engagement`, so picking a filter
  // doesn't visually re-tag the stale pre-filter rows during the ~250ms before
  // the server response lands.
  const [rowsEngagement, setRowsEngagement] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  // Selection is URL-driven (route param) so the chat is shareable + reopens on
  // refresh for any enrolled patient (off-page ids are hydrated by-id below).
  // Row clicks call onSelectPatient, which pushes /engage/:id.
  const selectedId = selectedPatientId ? String(selectedPatientId) : null;
  // The arrow walk reads this, not `selectedId`: a quick second key lands before the URL change
  // renders. Our own pushes queue in `pendingIdsRef`; an older one landing late is skipped, anything
  // else (our latest, Back, the sidebar) is adopted. The location key catches an A→B→A batch.
  const { key: locationKey } = useLocation();
  const selectedIdRef = useRef(selectedId);
  const pendingIdsRef = useRef([]);
  useLayoutEffect(() => {
    const pending = pendingIdsRef.current;
    const at = pending.indexOf(selectedId);
    if (at !== -1 && at < pending.length - 1) { pending.splice(0, at + 1); return; }
    pendingIdsRef.current = [];
    selectedIdRef.current = selectedId;
    // Back, the sidebar or a closed chat lets the held row go; our own click already moved the hold.
    setHeldUnreadId((held) => (held === selectedId ? held : null));
  }, [selectedId, locationKey]);
  const [_loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [callPatient, setCallPatient] = useState(null);
  const startCall = useCallStore(s => s.startCall);
  const demoMode = useDemoMode();
  const nowMs = useNow();
  const fetchDashboardView = useFetchDashboardView();
  // The sidebar badge counts this same roster; it refreshes when the list does.
  const refreshUnreadCount = useRefreshUnreadCount();
  const adjustUnreadCount = useAdjustUnreadCount();
  // .mutate is referentially stable in TanStack v5 (the mutation object is not).
  const markViewed = useMarkChatViewed().mutate;
  const markUnread = useMarkChatUnread().mutate;
  // Patient half of the recency sort; the care-team half rides on the roster row.
  // Refreshed by the same idle-gated interval as the roster (see below) — `refetch`
  // is referentially stable in TanStack v5, the query object is not.
  const inboundQuery = useLastInteractionDates(engageRosterScope().programid, { enabled: !!userRole });
  const inboundDates = inboundQuery.data;
  const refetchInboundDates = inboundQuery.refetch;

  // Toggle a conversation's unread state from the row kebab. Optimistically flips
  // the local row (the Engage list is imperative local state, not a TanStack query,
  // so the mutation's dashboard invalidate doesn't refetch it) then fires the PATCH.
  const handleToggleUnread = useCallback((userid, makeUnread) => {
    setRows(prev => prev.map(r =>
      String(r.userid) === String(userid)
        ? { ...r, unread: { ...(r.unread || {}), has_unread: makeUnread } }
        : r
    ));
    // The kebab only offers the opposite of the row's state, so this always flips it.
    adjustUnreadCount(makeUnread ? 1 : -1);
    if (makeUnread) markUnread(userid);
    else markViewed(userid);
  }, [markUnread, markViewed, adjustUnreadCount]);

  // One way into a conversation — the row click and the arrow keys share it.
  const selectPatient = useCallback((p) => {
    if (!p) return;
    const id = String(p.userid);
    setHeldUnreadId((prev) => (p.unread?.has_unread || prev === id ? id : null));
    // Re-picking the open chat pushes nothing: a duplicate history entry makes Back look dead.
    if (id !== selectedIdRef.current && onSelectPatient) {
      selectedIdRef.current = id;
      pendingIdsRef.current.push(id);
      onSelectPatient(p.userid);
    }
    // Optimistically drop the list dot — ChatPanel's mount effect bumps
    // careteam_last_seen_at server-side.
    if (p.unread?.has_unread) {
      setRows(prev => prev.map(r => (
        String(r.userid) === String(p.userid)
          ? { ...r, unread: { ...(r.unread || {}), has_unread: false } }
          : r
      )));
      adjustUnreadCount(-1);
    }
  }, [onSelectPatient, adjustUnreadCount]);

  const isIdle = useIdleAware(IDLE_THRESHOLD_MS);
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);
  const headRef = useRef(null);
  const searchRef = useRef(null);

  // Live refs so the IntersectionObserver callback (mounted once) reads the
  // current filter / role / page boundary instead of a stale closure capture.
  const engagementRef  = useRef(engagement);
  const stepRef        = useRef(step);
  const queryRef       = useRef(query);
  const rowsLenRef     = useRef(0);
  const loadingMoreRef = useRef(false);
  const pageFetchCtrl  = useRef(null);
  useEffect(() => { engagementRef.current = engagement; }, [engagement]);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { queryRef.current = query;           }, [query]);
  useEffect(() => { rowsLenRef.current = rows.length;   }, [rows.length]);
  useEffect(() => { loadingMoreRef.current = loadingMore; }, [loadingMore]);

  // Server-driven label lookup. Builds a small {value → label} index from the
  // engagement filter so the row tag can render the human label without
  // a hardcoded client copy of the enum.
  const engagementLabels = useMemo(() => {
    const out = {};
    (engagementFilter?.options || []).forEach((opt) => { out[opt.value] = opt.label; });
    return out;
  }, [engagementFilter]);

  // Bucket per row, from the filter the rows were FETCHED for (not the live one). Unfiltered, only
  // `has_unread` is known per row — the payload carries no other bucket flags.
  const taggedRows = useMemo(() => {
    return rows.map((r) => {
      let reason = rowsEngagement;
      if (!reason && r.unread?.has_unread) reason = 'unread';
      return {
        ...r,
        engageReason: reason,
        engageReasonLabel: reason ? (engagementLabels[reason] ?? reason) : null,
      };
    });
  }, [rows, rowsEngagement, engagementLabels]);

  // A chat opened by URL (Reply in chat, Back, a refresh) is held too, once its row shows unread.
  useEffect(() => {
    if (!selectedId || selectedIdRef.current !== selectedId) return;
    if (rows.some((r) => String(r.userid) === selectedId && r.unread?.has_unread)) setHeldUnreadId(selectedId);
  }, [rows, selectedId]);

  // Most recent conversation first. Server order until the inbound map lands, else the roster
  // ranks on the care-team half alone and visibly reshuffles; server order is the
  // honest placeholder. `lastInteractionMs` is display-only — no unread fallback, so
  // an unknown (or clinic-line) time renders blank rather than invented.
  // Spec: docs/specs/2026-07-30-engage-conversation-ordering.md
  const orderedRows = useMemo(() => {
    if (!inboundDates) return taggedRows;
    const stamped = taggedRows.map((p) => ({ ...p, lastInteractionMs: conversationTimeMs(p, inboundDates) }));
    return stamped.sort(byLastInteractionDesc(inboundDates, undefined, heldUnreadId));
  }, [taggedRows, inboundDates, heldUnreadId]);

  // Load page 0 (or after engagement/query change). Cancellable.
  const loadedOnceRef = useRef(false);
  const loadFirstPage = useCallback(async (signal, { fresh = false } = {}) => {
    if (!userRole) return;
    // Cancel any in-flight pagination fetch — an engagement/query/role change
    // invalidates whatever page-N was reaching for.
    if (pageFetchCtrl.current) {
      pageFetchCtrl.current.abort();
      pageFetchCtrl.current = null;
    }
    setLoading(true);
    try {
      const { role, ...scope } = engageRosterScope();
      const res = await fetchDashboardView(role, {
        ...scope,
        engagement,
        selectedFilters: step ? { open_task: [step] } : undefined,
        search: query.trim().slice(0, 100) || undefined,
        sort: 'unread',
        pagination: { limit: PAGE_SIZE, offset: 0 },
      }, { fresh });
      if (signal?.aborted) return;
      if (res?.status === 'success' && Array.isArray(res.rows)) {
        const mapped = res.rows.map(rowToPatient);
        setRows(mapped);
        setRowsEngagement(engagement);
        setTotalCount(res.pagination?.total_count ?? mapped.length);
        // Pull the engagement filter entry from `data.filters[]`. The server
        // is the source of truth for option labels, priority_order, AND
        // per-option counts (no parallel `kpis` block).
        const engFilter = (res.filters || []).find((f) => f.key === 'engagement') ?? null;
        setEngagementFilter(engFilter ?? { key: 'engagement', options: [] });
        setSteps(journeyGroups(res.filters));
        setHasMore(!!res.pagination?.has_more);
        setError(null);
      } else {
        setRows([]);
        setRowsEngagement(engagement);
        setTotalCount(0);
        // Keep the last good options (an open filter menu must not empty under the user);
        // an empty object rather than null so a failed FIRST fetch doesn't trap the skeleton.
        setEngagementFilter((prev) => prev ?? { key: 'engagement', options: [] });
        setHasMore(false);
        setError(res?.reason || 'Failed to load patient list');
      }
    } catch (err) {
      if (signal?.aborted) return;
      console.warn('[EngageWorkspace] loadFirstPage failed', err);
      setRows([]);
      setRowsEngagement(engagement);
      setTotalCount(0);
      setEngagementFilter((prev) => prev ?? { key: 'engagement', options: [] });
      setHasMore(false);
      setError(err?.message || 'Failed to load patient list');
    } finally {
      if (!signal?.aborted) { setLoading(false); loadedOnceRef.current = true; }
    }
  }, [userRole, engagement, step, query, fetchDashboardView]);

  // Initial fetch + debounced refetch on engagement/query/role change.
  useEffect(() => {
    if (!userRole) return;
    const ctrl = new AbortController();
    // A radio group re-checks on every arrow key; settle before fetching (the first load doesn't wait).
    const delay = query.trim().length > 0 ? 250 : (loadedOnceRef.current ? 150 : 0);
    const handle = setTimeout(() => loadFirstPage(ctrl.signal), delay);
    return () => { ctrl.abort(); clearTimeout(handle); };
  }, [loadFirstPage, userRole, engagement, step, query]);

  // Background refresh every 60s. Pause when the operator is idle — this gate covers
  // the inbound-dates read too (an unbounded whole-table fetch, so it must never
  // self-poll past the roster it orders).
  const intervalFetchCtrl = useRef(null);
  useEffect(() => {
    if (!userRole) return;
    const id = setInterval(() => {
      if (isIdle()) return;
      if (intervalFetchCtrl.current) intervalFetchCtrl.current.abort();
      const ctrl = new AbortController();
      intervalFetchCtrl.current = ctrl;
      loadFirstPage(ctrl.signal, { fresh: true });
      refetchInboundDates();
      refreshUnreadCount();
    }, REFRESH_INTERVAL_MS);
    return () => {
      clearInterval(id);
      if (intervalFetchCtrl.current) {
        intervalFetchCtrl.current.abort();
        intervalFetchCtrl.current = null;
      }
    };
  }, [loadFirstPage, userRole, isIdle, refetchInboundDates, refreshUnreadCount]);

  // Landing here: the badge may be up to a minute older than the list being fetched.
  useEffect(() => {
    if (userRole) refreshUnreadCount();
  }, [userRole, refreshUnreadCount]);

  // Infinite scroll — observer mounted ONCE per hasMore flip; filter values
  // read from refs so the callback never fires with a stale offset/engagement.
  // Rooted on the list scroller so the 200px prefetch margin applies to it, not the viewport.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      if (loadingMoreRef.current) return;
      const ctrl = new AbortController();
      pageFetchCtrl.current = ctrl;
      setLoadingMore(true);
      const { role, ...scope } = engageRosterScope();
      fetchDashboardView(role, {
        ...scope,
        engagement: engagementRef.current,
        selectedFilters: stepRef.current ? { open_task: [stepRef.current] } : undefined,
        search: queryRef.current.trim().slice(0, 100) || undefined,
        sort: 'unread',
        pagination: { limit: PAGE_SIZE, offset: rowsLenRef.current },
      }).then((res) => {
        if (ctrl.signal.aborted) return;
        if (res?.status === 'success' && Array.isArray(res.rows)) {
          const mapped = res.rows.map(rowToPatient);
          setRows((prev) => [...prev, ...mapped]);
          setHasMore(!!res.pagination?.has_more);
        }
      }).catch((err) => {
        if (ctrl.signal.aborted) return;
        console.warn('[EngageWorkspace] loadNextPage failed', err);
      }).finally(() => {
        if (!ctrl.signal.aborted) setLoadingMore(false);
        if (pageFetchCtrl.current === ctrl) pageFetchCtrl.current = null;
      });
    }, { root: scrollRef.current, rootMargin: '200px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, fetchDashboardView]);

  // The sticky header's height changes when filter tokens appear; scroll-padding follows it,
  // so an arrow-key scrollIntoView never parks the selected row under the header.
  useEffect(() => {
    const head = headRef.current;
    const scroller = scrollRef.current;
    if (!head || !scroller || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => scroller.style.setProperty('--engage-head-h', `${head.offsetHeight}px`));
    ro.observe(head);
    return () => ro.disconnect();
  }, []);

  // A data attribute React doesn't render, so toggling it never re-renders the workspace.
  const onListScroll = (e) => {
    const head = headRef.current;
    if (!head) return;
    const scrolled = e.currentTarget.scrollTop > 0 ? 'true' : 'false';
    if (head.dataset.scrolled !== scrolled) head.dataset.scrolled = scrolled;
  };

  // Selected patient — first look in the loaded page.
  const rosterSelected = useMemo(() => {
    if (!selectedId) return null;
    return orderedRows.find((p) => String(p.userid) === String(selectedId)) || null;
  }, [selectedId, orderedRows]);

  // Deep-link hydration — a shared /engage/:id may point at a patient beyond the
  // loaded roster page (or filtered out). Fetch that patient's identity by id so
  // the chat opens anyway. Gated on the first roster response having landed
  // (engagementFilter !== null) so we don't fire for a patient that's about to
  // appear on page 1; shares the PATIENT_KEYS cache.
  const needHydration = !!selectedId && !rosterSelected && engagementFilter !== null;
  const hydrateQuery = usePatient(needHydration ? selectedId : null);

  // ↑/↓ walk the list from anywhere. The chat composer autofocuses, so an EMPTY composer
  // still walks it; a started message, other inputs, a modal or an open Engage menu keep the keys.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName;
      const typing = el?.isContentEditable || tag === 'SELECT'
        || (tag === 'TEXTAREA' && el.value?.trim().length > 0)
        || (tag === 'INPUT' && !el.classList?.contains('patient-list-search'));
      if (typing) return;
      if (document.querySelector(`.modal-overlay, .slide-over-backdrop, ${POPOVER_OPEN}`)) return;
      if (el?.closest?.('[role="menu"], [role="dialog"]')) return;
      const nextId = stepSelection(orderedRows.map((p) => p.userid), selectedIdRef.current, e.key === 'ArrowDown' ? 1 : -1);
      if (nextId == null) return;
      e.preventDefault(); // else the page scrolls under the selection
      selectPatient(orderedRows.find((p) => String(p.userid) === String(nextId)));
      // `nearest` keeps the row in the list without yanking the page around.
      document.querySelector(`.patient-list-scroll [data-userid="${nextId}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [orderedRows, selectPatient]);

  // `/` jumps to the search unless focus is already in a field (the composer keeps its slash).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.isComposing) return;
      const el = e.target;
      const tag = el?.tagName;
      if (el?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (document.querySelector(`.modal-overlay, .slide-over-backdrop, ${POPOVER_OPEN}`)) return;
      e.preventDefault();
      searchRef.current?.focus({ preventScroll: true });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selectedPatient = useMemo(() => {
    if (rosterSelected) return rosterSelected;
    if (needHydration && hydrateQuery.data) {
      const d = hydrateQuery.data;
      return {
        userid: d.userid ?? selectedId,
        firstname: d.firstname || '',
        lastname: d.lastname || '',
        phone_number: d.phone_number ?? d.phone?.number ?? null,
        lastSystolic: null,
        lastDiastolic: null,
        lastEpoch: null,
        ehr_patient_id: d.ehr_patient_id || null,
        unread: null,
        engageReason: null,
        engageReasonLabel: null,
      };
    }
    return null;
  }, [rosterSelected, needHydration, hydrateQuery.data, selectedId]);

  const smartMessages = useMemo(() => {
    if (!selectedPatient) return [];
    return getSmartMessages(selectedPatient, selectedPatient.engageReason);
  }, [selectedPatient]);

  // Roster rows carry the phone; a deep-linked patient is hydrated from getpatient, which has
  // none for ACCESS patients, so Spruce reads the patient record there (and only there).
  const selectedRecord = usePatientRecord(Constants.SPRUCE_ORG_ID && needHydration ? selectedId : null);
  const openTwilioCall = useCallback(() => {
    if (selectedPatient) setCallPatient(selectedPatient);
  }, [selectedPatient]);
  const call = useSpruceCall(
    resolvePatientPhone(selectedRecord.data, null, selectedPatient),
    selectedRecord.isLoading,
    openTwilioCall,
  );

  // First-load sentinel — true until the first /views/dashboard response lands.
  const isFirstLoad = engagementFilter === null;

  // Engagement buckets in the server's `priority_order` (one bucket is picked at a time), else option order.
  const bucketList = useMemo(() => {
    if (!engagementFilter?.options) return [];
    const order = engagementFilter.priority_order;
    if (Array.isArray(order) && order.length > 0) {
      const byValue = Object.fromEntries(engagementFilter.options.map((o) => [o.value, o]));
      return order.map((v) => byValue[v]).filter(Boolean);
    }
    return engagementFilter.options;
  }, [engagementFilter]);

  // Steps with nobody waiting are dropped (unlike the board, which keeps the row stable):
  // picking one would only empty the list.
  const stepList = steps.filter((g) => g.count > 0 || g.value === step);

  // A filter change resets the scroll: under a sticky header there is no other cue the list changed.
  const toTop = () => { if (scrollRef.current) scrollRef.current.scrollTop = 0; };
  const pickEngagement = (value) => { setEngagement(value); toTop(); };
  const pickStep = (value) => { setStep(value); toTop(); };
  const clearFilters = () => { setEngagement(null); setStep(null); toTop(); };
  const clearEverything = () => { clearFilters(); setQuery(''); searchRef.current?.focus({ preventScroll: true }); };
  // The token's own button unmounts on click; hand focus to the search instead of <body>.
  const removeToken = (kind) => {
    if (kind === 'bucket') setEngagement(null);
    else setStep(null);
    toTop();
    searchRef.current?.focus({ preventScroll: true });
  };

  const activeCount = (engagement ? 1 : 0) + (step ? 1 : 0);
  const bucketLabel = engagement
    ? (bucketList.find((o) => o.value === engagement)?.label ?? engagementLabels[engagement] ?? engagement)
    : null;
  const stepLabel = step ? (steps.find((g) => g.value === step)?.label ?? step) : null;
  const filtered = activeCount > 0 || query.trim().length > 0;
  const subtitle = engageListSubtitle({
    loaded: !isFirstLoad,
    error,
    rows,
    total: totalCount,
    hasMore,
    filtered,
    serverUnread: bucketList.find((o) => o.value === 'unread')?.count ?? null,
  });

  const selectedTone = selectedPatient?.engageReason ? toneOf(selectedPatient.engageReason) : null;

  return (
    <div className="patient-workspace">
      {/* ── Left panel: server-paged patient list ── */}
      <div className="patient-list-panel">
        <div className="patient-list-scroll engage-list-scroll" ref={scrollRef} onScroll={onListScroll}>
          {/* Rendered once, above every list state: the search must stay mounted while results change. */}
          <div className="engage-list-head" ref={headRef}>
            <div className="engage-list-head__bar">
              <div className="engage-list-head__title">
                <h2 className="engage-list-head__h">Conversations</h2>
                <span className="detail1 text-secondary engage-list-head__sub">{subtitle || ' '}</span>
              </div>
              <EngageFilterMenu
                buckets={bucketList}
                bucket={engagement}
                onBucket={pickEngagement}
                steps={stepList}
                step={step}
                onStep={pickStep}
                onClear={clearFilters}
                activeCount={activeCount}
                disabled={isFirstLoad}
              />
            </div>
            <div className="engage-search">
              <svg className="engage-search__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <circle cx="11" cy="11" r="6.5" /><path d="m16 16 4 4" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                className="patient-list-search"
                placeholder="Search"
                aria-label="Search patients"
                autoComplete="off"
                value={query}
                onChange={(e) => { setQuery(e.target.value); toTop(); }}
              />
              {query && (
                <button
                  type="button"
                  className="engage-search__clear"
                  aria-label="Clear search"
                  onClick={() => { setQuery(''); searchRef.current?.focus({ preventScroll: true }); }}
                >
                  <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6 6 18" />
                  </svg>
                </button>
              )}
            </div>
            {activeCount > 0 && (
              <div className="engage-filter-tokens">
                {engagement && (
                  <button key="bucket" type="button" className="engage-filter-token" aria-label={`Remove filter: ${bucketLabel}`} onClick={() => removeToken('bucket')}>
                    {bucketLabel}<span aria-hidden="true">×</span>
                  </button>
                )}
                {step && (
                  <button key="step" type="button" className="engage-filter-token" aria-label={`Remove filter: ${stepLabel}`} onClick={() => removeToken('step')}>
                    {`${stepEmoji(step)} ${stepLabel}`}<span aria-hidden="true">×</span>
                  </button>
                )}
                {activeCount > 1 && (
                  <button type="button" className="filter-clear" onClick={() => { clearFilters(); searchRef.current?.focus({ preventScroll: true }); }}>
                    Clear all
                  </button>
                )}
              </div>
            )}
          </div>

          {isFirstLoad ? (
            <div className="engage-list" aria-busy="true" aria-label="Loading patients">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="engage-row is-skeleton">
                  <span />
                  <div className="skeleton-circle" style={{ width: 40, height: 40 }} />
                  <div className="engage-row__body engage-row__skeleton">
                    <div className="skeleton-line" style={{ width: '55%', height: 14 }} />
                    <div className="skeleton-line" style={{ width: '90%' }} />
                    <div className="skeleton-line" style={{ width: '35%', height: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          ) : orderedRows.length === 0 ? (
            <div className="engage-list-empty">
              <p className="body2 text-secondary">
                {error || (filtered ? 'No conversations match.' : 'No patients to display')}
              </p>
              {!error && filtered && (
                <button type="button" className="filter-clear" onClick={clearEverything}>Clear search and filters</button>
              )}
            </div>
          ) : (
            <div className="engage-list">
              {orderedRows.map((p) => (
                <EngagePatientRow
                  key={p.userid}
                  patient={p}
                  isSelected={String(p.userid) === String(selectedId)}
                  replied={careTeamSpokeLast(p, inboundDates)}
                  nowMs={nowMs}
                  onToggleUnread={handleToggleUnread}
                  onClick={() => selectPatient(p)}
                  demoMode={demoMode}
                />
              ))}
              {hasMore && (
                <div ref={sentinelRef} style={{ padding: '20px', textAlign: 'center' }}>
                  <p className="detail1 text-secondary" style={{ margin: 0 }}>
                    {loadingMore ? 'Loading more…' : ''}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── Right panel: chat + smart actions ── */}
      <div className="patient-detail-panel" style={{ display: 'flex', flexDirection: 'column', padding: 0 }}>
        {!selectedPatient ? (
          <div style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 12,
          }}>
            {/* Deep-linked patient still resolving / unresolvable — don't flash
                the generic "select a patient" copy when the URL names one. */}
            {selectedId && (isFirstLoad || (needHydration && hydrateQuery.isLoading)) ? (
              <>
                <div className="loading-spinner" />
                <p className="body2 text-secondary" style={{ margin: 0 }}>Loading patient…</p>
              </>
            ) : needHydration ? (
              <p className="body2 text-secondary" style={{ margin: 0 }}>Couldn’t load this patient.</p>
            ) : (
              <>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--wi-text-placeholder)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                <p className="body2 text-secondary" style={{ margin: 0 }}>Select a patient to start engaging</p>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={{
              padding: '16px 24px',
              borderBottom: '1px solid var(--wi-divider)',
              background: 'var(--wi-background-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              flexShrink: 0,
            }}>
              <div
                role={onOpenPatientFile ? 'button' : undefined}
                tabIndex={onOpenPatientFile ? 0 : undefined}
                aria-label={onOpenPatientFile ? `Open patient file for ${selectedPatient.firstname} ${selectedPatient.lastname}` : undefined}
                onClick={onOpenPatientFile ? () => onOpenPatientFile() : undefined}
                onKeyDown={onOpenPatientFile ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenPatientFile(); }
                } : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0,
                  cursor: onOpenPatientFile ? 'pointer' : 'default',
                }}
              >
                <Media
                  size={40}
                  alt=""
                  fallback={`${selectedPatient.firstname?.[0] ?? ''}${selectedPatient.lastname?.[0] ?? ''}`.toUpperCase() || '?'}
                  className={demoMode ? 'demo-blur' : undefined}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span className={`header4${demoMode ? ' demo-blur' : ''}`} style={{ textTransform: 'capitalize' }}>
                      {selectedPatient.firstname} {selectedPatient.lastname}
                    </span>
                    <span className="detail1 text-secondary">{selectedPatient.userid}</span>
                    {selectedTone && selectedPatient.engageReasonLabel && (
                      <span className={`engage-tag engage-tag--${selectedTone}`}>
                        {selectedPatient.engageReasonLabel}
                      </span>
                    )}
                  </div>
                  <div className="detail1 text-secondary" style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      BP: <BpValue systolic={selectedPatient.lastSystolic} diastolic={selectedPatient.lastDiastolic} />
                    </span>
                    <span>
                      Last reading: {timeAgo(selectedPatient.lastEpoch, nowMs) || 'Never'}
                    </span>
                  </div>
                </div>
              </div>
              <Button
                className="engage-call-btn"
                size={Size.MINI}
                type={Type.PRIMARY}
                icon={WiconsName.FEATURE_PHONECALL}
                iconLibrary={IconsLibrary.WICONS}
                ariaLabel={call.pending ? call.label : `Call ${selectedPatient.firstname} ${selectedPatient.lastname}${call.viaSpruce ? ' in Spruce (opens a new tab)' : ''}`}
                disabled={call.pending}
                onClick={call.onCall}
              >
                Call
              </Button>
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
              <ChatPanel
                key={selectedPatient.userid}
                patient={selectedPatient}
                currentUser={currentUser}
                embedded
                autoFocus
                smartMessages={smartMessages}
              />
            </div>
          </>
        )}
      </div>

      {callPatient && (
        <CallModal
          patient={callPatient}
          onClose={() => setCallPatient(null)}
          onConfirm={() => startCall(callPatient)}
        />
      )}
    </div>
  );
}
